const ENCODED_BYTE_PATTERN = /^%[0-9a-fA-F]{2}$/
const INVALID_PERCENT_ENCODING_PATTERN = /%(?![0-9a-fA-F]{2})/
const CONTROL_CHARACTER_PATTERN = /[\u0000-\u001F\u007F]/
const MAX_PERCENT_DECODE_DEPTH = 3
const KK_FILE_VIEW_SAFE_CHAR_PATTERN = /^[A-Za-z0-9+*._-]$/
const FILE_NAME_TOKEN_PATTERN =
  /%[0-9a-fA-F]{2}|[\uD800-\uDBFF][\uDC00-\uDFFF]|[\s\S]/g
const SIGNED_URL_QUERY_PARAM_PATTERN =
  /^(?:signature$|ossaccesskeyid$|x-oss-|x-amz-)/i

const INVALID_FILE_URL_MESSAGE = '文件地址无效，请重新上传'
const UNSAFE_FILE_PATH_MESSAGE =
  '文件地址包含不安全的路径片段，已阻止预览，请修改文件名并重新上传'

const createPreviewUrlError = (message: string) => {
  const error = new Error(message)
  error.name = 'PreviewUrlError'
  return error
}

const findFirstDelimiter = (
  value: string,
  startIndex: number,
  delimiters: string,
) => {
  for (let index = startIndex; index < value.length; index += 1) {
    if (delimiters.indexOf(value.charAt(index)) >= 0) {
      return index
    }
  }

  return -1
}

const extractRawPath = (fileUrl: string) => {
  const schemeMatch = /^[A-Za-z][A-Za-z0-9+.-]*:\/\//.exec(fileUrl)
  if (!schemeMatch) {
    throw createPreviewUrlError(INVALID_FILE_URL_MESSAGE)
  }

  const authorityStart = schemeMatch[0].length
  const pathOrQueryStart = findFirstDelimiter(fileUrl, authorityStart, '/\\?#')
  const authorityEnd =
    pathOrQueryStart === -1 ? fileUrl.length : pathOrQueryStart

  if (authorityEnd === authorityStart) {
    throw createPreviewUrlError(INVALID_FILE_URL_MESSAGE)
  }

  if (
    pathOrQueryStart === -1 ||
    fileUrl.charAt(pathOrQueryStart) === '?' ||
    fileUrl.charAt(pathOrQueryStart) === '#'
  ) {
    return ''
  }

  const pathEnd = findFirstDelimiter(fileUrl, pathOrQueryStart, '?#')
  return fileUrl.slice(
    pathOrQueryStart,
    pathEnd === -1 ? fileUrl.length : pathEnd,
  )
}

const decodePercentEncodedAscii = (value: string) => {
  return value.replace(/%([0-7][0-9a-fA-F])/g, (_match, hex: string) =>
    String.fromCharCode(parseInt(hex, 16)),
  )
}

const encodeBase64Utf8 = (value: string) => {
  const utf8ByteString = encodeURIComponent(value).replace(
    /%([0-9a-fA-F]{2})/g,
    (_match, hex: string) => String.fromCharCode(parseInt(hex, 16)),
  )
  return btoa(utf8ByteString)
}

const hasUnsafePathSegment = (path: string) => {
  return path
    .replace(/\\/g, '/')
    .split('/')
    .some((segment) => segment === '.' || segment === '..')
}

const assertSafePathRepresentation = (pathRepresentation: string) => {
  if (CONTROL_CHARACTER_PATTERN.test(pathRepresentation)) {
    throw createPreviewUrlError(INVALID_FILE_URL_MESSAGE)
  }
  if (hasUnsafePathSegment(pathRepresentation)) {
    throw createPreviewUrlError(UNSAFE_FILE_PATH_MESSAGE)
  }
}

const validateRawPath = (rawPath: string) => {
  if (
    INVALID_PERCENT_ENCODING_PATTERN.test(rawPath) ||
    CONTROL_CHARACTER_PATTERN.test(rawPath)
  ) {
    throw createPreviewUrlError(INVALID_FILE_URL_MESSAGE)
  }

  try {
    decodeURIComponent(rawPath)
  } catch {
    throw createPreviewUrlError(INVALID_FILE_URL_MESSAGE)
  }

  let pathRepresentation = rawPath
  assertSafePathRepresentation(pathRepresentation)

  for (
    let decodeCount = 0;
    decodeCount < MAX_PERCENT_DECODE_DEPTH;
    decodeCount += 1
  ) {
    const decodedPath = decodePercentEncodedAscii(pathRepresentation)
    if (decodedPath === pathRepresentation) {
      return
    }

    assertSafePathRepresentation(decodedPath)
    pathRepresentation = decodedPath
  }

  if (decodePercentEncodedAscii(pathRepresentation) !== pathRepresentation) {
    throw createPreviewUrlError(UNSAFE_FILE_PATH_MESSAGE)
  }
}

const encodeFileNameToken = (token: string) => {
  if (ENCODED_BYTE_PATTERN.test(token)) {
    return `%${token.slice(1).toUpperCase()}`
  }
  if (KK_FILE_VIEW_SAFE_CHAR_PATTERN.test(token)) {
    return token
  }
  return encodeURIComponent(token).replace(
    /[!'()~]/g,
    (char) => `%${char.charCodeAt(0).toString(16).toUpperCase()}`,
  )
}

const isDotToken = (token: string) => {
  return token === '.' || token.toUpperCase() === '%2E'
}

const normalizeFileName = (fileName: string) => {
  const tokens = fileName.match(FILE_NAME_TOKEN_PATTERN) || []
  const normalizedTokens: string[] = []

  for (let index = 0; index < tokens.length; index += 1) {
    if (!isDotToken(tokens[index])) {
      normalizedTokens.push(encodeFileNameToken(tokens[index]))
      continue
    }

    let dotRunEnd = index + 1
    while (dotRunEnd < tokens.length && isDotToken(tokens[dotRunEnd])) {
      dotRunEnd += 1
    }

    const encodedDot = dotRunEnd - index > 1 ? '%2E' : undefined
    for (let dotIndex = index; dotIndex < dotRunEnd; dotIndex += 1) {
      normalizedTokens.push(encodedDot || encodeFileNameToken(tokens[dotIndex]))
    }
    index = dotRunEnd - 1
  }

  return normalizedTokens.join('')
}

const isSignedFileUrl = (url: URL) => {
  let isSigned = false
  url.searchParams.forEach((_value, key) => {
    if (SIGNED_URL_QUERY_PARAM_PATTERN.test(key)) {
      isSigned = true
    }
  })
  return isSigned
}

export const normalizeFileUrl = (fileUrl: string) => {
  const rawPath = extractRawPath(fileUrl)
  validateRawPath(rawPath)

  let url: URL
  try {
    url = new URL(fileUrl)
  } catch {
    throw createPreviewUrlError(INVALID_FILE_URL_MESSAGE)
  }

  if (!['http:', 'https:'].includes(url.protocol) || !url.hostname) {
    throw createPreviewUrlError(INVALID_FILE_URL_MESSAGE)
  }

  // 带签名的 URL 不能改写 path，否则可能导致签名失效。
  if (isSignedFileUrl(url)) {
    return fileUrl
  }

  const fileNameStart = url.pathname.lastIndexOf('/') + 1
  const fileName = url.pathname.slice(fileNameStart)

  // kkFileView v5 按 Java URLEncoder 规则判断文件名是否已编码。
  const normalizedFileName = normalizeFileName(fileName)
  url.pathname = `${url.pathname.slice(0, fileNameStart)}${normalizedFileName}`

  return url.href
}

export const encodePreviewUrl = (fileUrl: string) => {
  return encodeURIComponent(encodeBase64Utf8(normalizeFileUrl(fileUrl)))
}

export const buildPreviewUrl = (previewServerUrl: string, fileUrl: string) => {
  return `${previewServerUrl}${encodePreviewUrl(fileUrl)}`
}

export type PreviewUrlResult =
  | { success: true; previewUrl: string }
  | { success: false; message: string }

export const tryBuildPreviewUrl = (
  previewServerUrl: string,
  fileUrl: string,
): PreviewUrlResult => {
  try {
    return {
      success: true,
      previewUrl: buildPreviewUrl(previewServerUrl, fileUrl),
    }
  } catch (error) {
    return {
      success: false,
      message:
        error instanceof Error && error.name === 'PreviewUrlError'
          ? error.message
          : INVALID_FILE_URL_MESSAGE,
    }
  }
}
const ENCODED_BYTE_PATTERN = /^%[0-9a-fA-F]{2}$/
const INVALID_PERCENT_ENCODING_PATTERN = /%(?![0-9a-fA-F]{2})/
const CONTROL_CHARACTER_PATTERN = /[\u0000-\u001F\u007F]/
const MAX_PERCENT_DECODE_DEPTH = 3
const KK_FILE_VIEW_SAFE_CHAR_PATTERN = /^[A-Za-z0-9+*._-]$/
const FILE_NAME_TOKEN_PATTERN =
  /%[0-9a-fA-F]{2}|[\uD800-\uDBFF][\uDC00-\uDFFF]|[\s\S]/g
const SIGNED_URL_QUERY_PARAM_PATTERN =
  /^(?:signature$|ossaccesskeyid$|x-oss-|x-amz-)/i

const INVALID_FILE_URL_MESSAGE = '文件地址无效，请重新上传'
const UNSAFE_FILE_PATH_MESSAGE =
  '文件地址包含不安全的路径片段，已阻止预览，请修改文件名并重新上传'

const createPreviewUrlError = (message: string) => {
  const error = new Error(message)
  error.name = 'PreviewUrlError'
  return error
}

const findFirstDelimiter = (
  value: string,
  startIndex: number,
  delimiters: string,
) => {
  for (let index = startIndex; index < value.length; index += 1) {
    if (delimiters.indexOf(value.charAt(index)) >= 0) {
      return index
    }
  }

  return -1
}

const extractRawPath = (fileUrl: string) => {
  const schemeMatch = /^[A-Za-z][A-Za-z0-9+.-]*:\/\//.exec(fileUrl)
  if (!schemeMatch) {
    throw createPreviewUrlError(INVALID_FILE_URL_MESSAGE)
  }

  const authorityStart = schemeMatch[0].length
  const pathOrQueryStart = findFirstDelimiter(fileUrl, authorityStart, '/\\?#')
  const authorityEnd =
    pathOrQueryStart === -1 ? fileUrl.length : pathOrQueryStart

  if (authorityEnd === authorityStart) {
    throw createPreviewUrlError(INVALID_FILE_URL_MESSAGE)
  }

  if (
    pathOrQueryStart === -1 ||
    fileUrl.charAt(pathOrQueryStart) === '?' ||
    fileUrl.charAt(pathOrQueryStart) === '#'
  ) {
    return ''
  }

  const pathEnd = findFirstDelimiter(fileUrl, pathOrQueryStart, '?#')
  return fileUrl.slice(
    pathOrQueryStart,
    pathEnd === -1 ? fileUrl.length : pathEnd,
  )
}

const decodePercentEncodedAscii = (value: string) => {
  return value.replace(/%([0-7][0-9a-fA-F])/g, (_match, hex: string) =>
    String.fromCharCode(parseInt(hex, 16)),
  )
}

const encodeBase64Utf8 = (value: string) => {
  const utf8ByteString = encodeURIComponent(value).replace(
    /%([0-9a-fA-F]{2})/g,
    (_match, hex: string) => String.fromCharCode(parseInt(hex, 16)),
  )
  return btoa(utf8ByteString)
}

const hasUnsafePathSegment = (path: string) => {
  return path
    .replace(/\\/g, '/')
    .split('/')
    .some((segment) => segment === '.' || segment === '..')
}

const assertSafePathRepresentation = (pathRepresentation: string) => {
  if (CONTROL_CHARACTER_PATTERN.test(pathRepresentation)) {
    throw createPreviewUrlError(INVALID_FILE_URL_MESSAGE)
  }
  if (hasUnsafePathSegment(pathRepresentation)) {
    throw createPreviewUrlError(UNSAFE_FILE_PATH_MESSAGE)
  }
}

const validateRawPath = (rawPath: string) => {
  if (
    INVALID_PERCENT_ENCODING_PATTERN.test(rawPath) ||
    CONTROL_CHARACTER_PATTERN.test(rawPath)
  ) {
    throw createPreviewUrlError(INVALID_FILE_URL_MESSAGE)
  }

  try {
    decodeURIComponent(rawPath)
  } catch {
    throw createPreviewUrlError(INVALID_FILE_URL_MESSAGE)
  }

  let pathRepresentation = rawPath
  assertSafePathRepresentation(pathRepresentation)

  for (
    let decodeCount = 0;
    decodeCount < MAX_PERCENT_DECODE_DEPTH;
    decodeCount += 1
  ) {
    const decodedPath = decodePercentEncodedAscii(pathRepresentation)
    if (decodedPath === pathRepresentation) {
      return
    }

    assertSafePathRepresentation(decodedPath)
    pathRepresentation = decodedPath
  }

  if (decodePercentEncodedAscii(pathRepresentation) !== pathRepresentation) {
    throw createPreviewUrlError(UNSAFE_FILE_PATH_MESSAGE)
  }
}

const encodeFileNameToken = (token: string) => {
  if (ENCODED_BYTE_PATTERN.test(token)) {
    return `%${token.slice(1).toUpperCase()}`
  }
  if (KK_FILE_VIEW_SAFE_CHAR_PATTERN.test(token)) {
    return token
  }
  return encodeURIComponent(token).replace(
    /[!'()~]/g,
    (char) => `%${char.charCodeAt(0).toString(16).toUpperCase()}`,
  )
}

const isDotToken = (token: string) => {
  return token === '.' || token.toUpperCase() === '%2E'
}

const normalizeFileName = (fileName: string) => {
  const tokens = fileName.match(FILE_NAME_TOKEN_PATTERN) || []
  const normalizedTokens: string[] = []

  for (let index = 0; index < tokens.length; index += 1) {
    if (!isDotToken(tokens[index])) {
      normalizedTokens.push(encodeFileNameToken(tokens[index]))
      continue
    }

    let dotRunEnd = index + 1
    while (dotRunEnd < tokens.length && isDotToken(tokens[dotRunEnd])) {
      dotRunEnd += 1
    }

    const encodedDot = dotRunEnd - index > 1 ? '%2E' : undefined
    for (let dotIndex = index; dotIndex < dotRunEnd; dotIndex += 1) {
      normalizedTokens.push(encodedDot || encodeFileNameToken(tokens[dotIndex]))
    }
    index = dotRunEnd - 1
  }

  return normalizedTokens.join('')
}

const isSignedFileUrl = (url: URL) => {
  let isSigned = false
  url.searchParams.forEach((_value, key) => {
    if (SIGNED_URL_QUERY_PARAM_PATTERN.test(key)) {
      isSigned = true
    }
  })
  return isSigned
}

export const normalizeFileUrl = (fileUrl: string) => {
  const rawPath = extractRawPath(fileUrl)
  validateRawPath(rawPath)

  let url: URL
  try {
    url = new URL(fileUrl)
  } catch {
    throw createPreviewUrlError(INVALID_FILE_URL_MESSAGE)
  }

  if (!['http:', 'https:'].includes(url.protocol) || !url.hostname) {
    throw createPreviewUrlError(INVALID_FILE_URL_MESSAGE)
  }

  // 带签名的 URL 不能改写 path，否则可能导致签名失效。
  if (isSignedFileUrl(url)) {
    return fileUrl
  }

  const fileNameStart = url.pathname.lastIndexOf('/') + 1
  const fileName = url.pathname.slice(fileNameStart)

  // kkFileView v5 按 Java URLEncoder 规则判断文件名是否已编码。
  const normalizedFileName = normalizeFileName(fileName)
  url.pathname = `${url.pathname.slice(0, fileNameStart)}${normalizedFileName}`

  return url.href
}

export const encodePreviewUrl = (fileUrl: string) => {
  return encodeURIComponent(encodeBase64Utf8(normalizeFileUrl(fileUrl)))
}

export const buildPreviewUrl = (previewServerUrl: string, fileUrl: string) => {
  return `${previewServerUrl}${encodePreviewUrl(fileUrl)}`
}

export type PreviewUrlResult =
  | { success: true; previewUrl: string }
  | { success: false; message: string }

export const tryBuildPreviewUrl = (
  previewServerUrl: string,
  fileUrl: string,
): PreviewUrlResult => {
  try {
    return {
      success: true,
      previewUrl: buildPreviewUrl(previewServerUrl, fileUrl),
    }
  } catch (error) {
    return {
      success: false,
      message:
        error instanceof Error && error.name === 'PreviewUrlError'
          ? error.message
          : INVALID_FILE_URL_MESSAGE,
    }
  }
}
